// tienda/ModalidadesSection.jsx
// Two concise operating models without repeating the pricing section.

import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Box, CheckCircle2, Store, Warehouse } from "lucide-react";
import { brand, fadeUp } from "../shared/brand";

const SOLUTIONS = [
  {
    icon: Store,
    label: "Recojo en tienda",
    title: "Tu inventario se queda contigo.",
    text: "Fast Max recoge los pedidos preparados en tu local y los integra a la operación de entrega.",
    flow: ["Tu tienda", "Recojo Fast Max", "Cliente"],
    benefits: ["Mantienes control del stock", "Seguimiento por pedido", "COD cuando aplique"],
    color: brand.accentGold,
  },
  {
    icon: Warehouse,
    label: "Fulfillment Fast Max",
    title: "Nosotros almacenamos y despachamos.",
    text: "Tu inventario vive en Fast Max para preparar, empacar y despachar pedidos desde una sola operación.",
    flow: ["Almacén Fast Max", "Packing", "Cliente"],
    benefits: ["Inventario centralizado", "Preparación y packing", "Despacho same-day disponible"],
    color: brand.accentGreen,
    featured: true,
  },
];

export function ModalidadesSection() {
  return (
    <section id="soluciones" className="border-b bg-white scroll-mt-16" style={{ borderColor: brand.line }}>
      <div className="mx-auto max-w-7xl px-4 py-14 md:py-20">
        <motion.div {...fadeUp} className="mb-9 grid gap-4 md:grid-cols-[1fr_0.72fr] md:items-end">
          <div>
            <p className="mb-3 text-xs font-black uppercase tracking-[0.2em]" style={{ color: brand.primary }}>
              Soluciones operativas
            </p>
            <h2 className="max-w-2xl text-3xl font-black md:text-4xl" style={{ color: brand.ink }}>
              Despacha desde tu tienda o conecta tu stock a Fast Max.
            </h2>
          </div>
          <p className="text-sm leading-relaxed md:text-base" style={{ color: brand.muted }}>
            Elige dónde empieza la operación. La plataforma mantiene el pedido visible hasta su cierre.
          </p>
        </motion.div>

        <div className="grid gap-4 lg:grid-cols-2">
          {SOLUTIONS.map((solution, index) => {
            const Icon = solution.icon;

            return (
              <motion.article
                key={solution.label}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: index * 0.07 }}
                className="relative overflow-hidden rounded-[1.5rem] border p-6 sm:p-8"
                style={{
                  borderColor: solution.featured ? solution.color : brand.line,
                  background: solution.featured ? `${solution.color}08` : brand.bg,
                }}
              >
                <div className="flex items-start justify-between gap-4">
                  <span
                    className="grid size-12 place-items-center rounded-2xl"
                    style={{ background: `${solution.color}18`, color: solution.color }}
                  >
                    <Icon className="size-5" />
                  </span>
                  <span
                    className="rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-white"
                    style={{ background: solution.color }}
                  >
                    {solution.label}
                  </span>
                </div>

                <h3 className="mt-6 text-2xl font-black" style={{ color: brand.ink }}>{solution.title}</h3>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: brand.muted }}>{solution.text}</p>

                <div className="mt-6 flex flex-wrap items-center gap-2 rounded-2xl border bg-white p-3" style={{ borderColor: brand.line }}>
                  {solution.flow.map((step, stepIndex) => (
                    <React.Fragment key={step}>
                      <span className="inline-flex items-center gap-1.5 text-xs font-black" style={{ color: brand.ink }}>
                        {stepIndex === 1 && <Box className="size-3.5" style={{ color: solution.color }} />}
                        {step}
                      </span>
                      {stepIndex < solution.flow.length - 1 && (
                        <ArrowRight className="size-3.5" style={{ color: brand.muted }} aria-hidden="true" />
                      )}
                    </React.Fragment>
                  ))}
                </div>

                <ul className="mt-6 grid gap-2 sm:grid-cols-3">
                  {solution.benefits.map(benefit => (
                    <li key={benefit} className="flex items-start gap-2 text-xs font-bold" style={{ color: brand.muted }}>
                      <CheckCircle2 className="mt-0.5 size-3.5 shrink-0" style={{ color: solution.color }} />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
